<script setup lang="ts">
  import { ref, computed } from "vue";
  import { useI18n } from "vue-i18n";
  import { Dialog, Text, Button, IconBasketLarge, useNotification } from "@ui-kit";
  import { ResponseCode, shopZoneWipeApi } from "@src/api";

  const { zoneId, visible } = defineProps<{ zoneId?: string; visible: boolean }>();
  const emit = defineEmits<{ "click:close": [value: void]; "click:delete": [value: void] }>();

  const notification = useNotification();
  const { t } = useI18n();

  const is_pending_wipe = ref(false);
  const holdTimer = ref<number | null>(null);
  const holdProgress = ref(0);
  const buttonStyle = computed(() => ({
    "--hold-progress": `${holdProgress.value}%`,
  }));

  const handleDelete = async () => {
    if (zoneId) {
      is_pending_wipe.value = true;

      const result = await shopZoneWipeApi({ zone_id: zoneId });

      if (result.code === ResponseCode.OK) {
        notification.show({ title: t(`Товары удалены из зоны`) + ` (${result.items_count})`, type: "success" });
        emit("click:delete");
        emit("click:close");
      } else {
        notification.show({ title: t(`Ошибка при удалении товаров из зоны`), type: "error" });
      }

      is_pending_wipe.value = false;
    }
  };

  const startHolding = () => {
    if (is_pending_wipe.value) {
      return;
    }

    const startTime = Date.now();

    const animationFrame = () => {
      const elapsed = Date.now() - startTime;
      holdProgress.value = Math.min((elapsed / 2000) * 100, 100);

      if (elapsed < 2000) {
        holdTimer.value = requestAnimationFrame(animationFrame);
      }
    };

    holdTimer.value = requestAnimationFrame(animationFrame);
  };

  const stopHolding = () => {
    if (holdProgress.value >= 100) {
      handleDelete();
    }

    cancelHolding();
  };

  const cancelHolding = () => {
    if (holdTimer.value) {
      cancelAnimationFrame(holdTimer.value);
      holdTimer.value = null;
    }

    holdProgress.value = 0;
  };
</script>

<template>
  <Dialog :isOpen="visible" :closeOnOutsideClick="false" @close="emit('click:close')">
    <div class="delete-modal" data-testid="wipe-zone-modal">
      <div class="delete-modal__content">
        <IconBasketLarge />
        <div class="delete-modal__content-description">
          <Text font="body-bold-18">{{ $t("Удалить все товары из зоны") + "?" }}</Text>
          <Text font="body-regular-14">{{ $t("Это действие нельзя отменить") }}</Text>
        </div>
      </div>
      <div class="delete-modal__footer">
        <Button
          variant="negative"
          size="s"
          @mousedown="startHolding"
          @mouseup="stopHolding"
          @mouseleave="cancelHolding"
          @touchstart.prevent="startHolding"
          @touchend.prevent="stopHolding"
          @touchcancel="cancelHolding"
          data-testid="delete-items-modal-btn"
          class="hold-button"
          :isLoading="is_pending_wipe"
          :disabled="is_pending_wipe"
          :style="buttonStyle"
        >
          {{ $t("Удалить") }}
        </Button>
        <Button
          variant="secondary"
          size="s"
          @click="emit('click:close')"
          data-testid="cancel-delete-items-modal-btn"
          :disabled="is_pending_wipe"
        >
          {{ $t("Отменить") }}
        </Button>
      </div>
    </div>
  </Dialog>
</template>

<style scoped>
  .delete-modal {
    display: flex;
    flex-direction: column;
    gap: 24px;
    width: 328px;
    padding: 24px 20px 20px;
  }

  .delete-modal__content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
  }

  .delete-modal__content-description {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
  }

  .delete-modal__footer {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .delete-modal__footer button {
    width: 100%;
  }

  .hold-button {
    position: relative;
    overflow: hidden;
  }

  .hold-button::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: var(--hold-progress);
    height: 100%;
    background-color: var(--sf-base-background-modal);
    transition: width 0.1s linear;
    pointer-events: none;
  }
</style>
